<template>
  <TransitionRoot v-if="open" as="template" :show="open">
    <Dialog
      as="div"
      class="relative z-10"
      @close="
        open = false;
        stopSignOutTimer();
        resetIdleWarning();
      ">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <div class="fixed inset-0 bg-quinary/75 transition-opacity" />
      </TransitionChild>
      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center lg:items-center lg:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 lg:translate-y-0 lg:scale-95"
            enter-to="opacity-100 translate-y-0 lg:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 lg:scale-100"
            leave-to="opacity-0 translate-y-4 lg:translate-y-0 lg:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-background text-left shadow-xl transition-all lg:my-8 lg:w-full lg:max-w-lg">
              <div class="bg-background px-4 pt-5 pb-4 lg:p-6 lg:pb-4">
                <div class="lg:flex lg:items-start">
                  <div
                    class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 lg:mx-0 lg:h-10 lg:w-10">
                    <ExclamationTriangleIcon class="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div class="mt-3 text-center lg:mt-0 lg:ml-4 lg:text-left">
                    <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">{{
                      $t("Attention: inactive session")
                    }}</DialogTitle>
                    <div class="mt-2">
                      <p class="text-base text-focus">
                        {{
                          $t("For security reasons, you will be logged out after {amount} minutes of inactivity.", {
                            amount: 60,
                          })
                        }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bg-gray-50 px-4 py-3 lg:flex lg:flex-row-reverse lg:px-6">
                <button
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-base font-semibold text-septenary shadow-sm lg:ml-3 lg:w-auto"
                  @click="
                    open = false;
                    stopSignOutTimer();
                    resetIdleWarning();
                  ">
                  {{ $t("Stay here") }}
                </button>
                <button
                  ref="cancelButtonRef"
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-background px-3 py-2 text-base font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 lg:mt-0 lg:w-auto"
                  @click="
                    open = false;
                    userStore.signOut();
                  ">
                  Log out
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted } from "vue";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import { useUserStore } from "@/store/user";
import { useIdle, useTimeoutFn } from "@vueuse/core";

const TEST_IDLE = false;

const TIME_OUT_DURATION = TEST_IDLE ? 20_000 : 1000 * 60 * 60; // total inactivity before logout
const WARNING_DURATION = TEST_IDLE ? 10_000 : 1000 * 60 * 5; // warning countdown

const open = ref(false);
const userStore = useUserStore();

const { idle: idleWarning, reset: resetIdleWarning } = useIdle(TIME_OUT_DURATION - WARNING_DURATION, {
  events: ["mousedown", "keypress", "scroll", "touchstart", "click"],
  initialState: false,
});

const {
  start: startSignOutTimer,
  stop: stopSignOutTimer,
  isPending,
} = useTimeoutFn(() => userStore.signOut(), WARNING_DURATION, { immediate: false });

function closeModalAndCancelSignOut() {
  open.value = false;
  stopSignOutTimer();
}

watch(idleWarning, (isIdle) => {
  if (!isIdle) {
    if (open.value) closeModalAndCancelSignOut();
    return;
  }

  open.value = true;
  if (!isPending.value) startSignOutTimer();
});

onUnmounted(() => {
  stopSignOutTimer();
});
</script>